/**
 * CANONICAL CHAIN CONFIGURATION — single source of truth (spec §10).
 *
 * Every network value the app uses (chain id, RPC, explorer, factory address,
 * expected factory bytecode hash) lives here and nowhere else. On startup the
 * app reads the factory bytecode from `factoryAddress` and compares its
 * keccak256 hash to `expectedFactoryBytecodeHash`; a mismatch (or missing
 * bytecode) disables launches and shows a security error.
 *
 * Network values verified against the official Robinhood Chain documentation
 * (docs.robinhood.com/chain) — testnet chain id 46630, mainnet chain id 4663.
 *
 * MAINNET IS DEFINED BUT DISABLED IN THE UI FOR THIS BUILD. See
 * docs/MAINNET_READINESS_CHECKLIST.md. No mainnet deployment is authorised.
 *
 * TWO FACTORY CONTRACTS:
 *   factoryAddress          — Standard Token factory (fixed-supply ERC-20)
 *   fairLaunchFactoryAddress — Fair Launch factory (bonding-curve markets)
 *
 * These are separate contracts. NEVER confuse them.
 */

export type Hex = `0x${string}`;

/** Parse a decimal string to a non-negative bigint, or null if malformed. */
function parseBlock(value: string | undefined): bigint | null {
  if (value === undefined || value.trim() === "") return null;
  if (!/^\d+$/.test(value.trim())) return null;
  try {
    return BigInt(value.trim());
  } catch {
    return null;
  }
}

export interface ChainConfig {
  readonly key: "robinhoodTestnet" | "robinhoodMainnet";
  readonly label: string;
  readonly chainId: number;
  readonly rpcUrl: string;
  /** Optional approved fallback public RPC (never invented; env-configured). */
  readonly rpcFallbackUrl?: string;
  readonly explorerUrl: string;
  readonly explorerApiUrl: string;
  readonly nativeCurrency: { name: string; symbol: string; decimals: number };
  readonly faucetUrl?: string;

  // ── Standard Token factory (fixed-supply ERC-20) ────────────────────────────
  /** Deployed Standard Token factory address, or null until deployment. */
  readonly factoryAddress: Hex | null;
  /** Tx that created the Standard Token factory. */
  readonly factoryDeploymentTx: Hex | null;
  /**
   * Block the Standard Token factory was deployed at. History scans start here.
   * Required whenever `factoryAddress` is set.
   */
  readonly factoryDeploymentBlock: bigint | null;
  /**
   * keccak256 of the Standard Token factory's DEPLOYED (runtime) bytecode.
   * Regenerate with `npm --workspace @synapz/contracts run bytecode-hash`.
   */
  readonly expectedFactoryBytecodeHash: Hex | null;
  readonly factoryVersion: string;

  // ── Fair Launch factory (bonding-curve markets) ─────────────────────────────
  /** Deployed Fair Launch factory address, or null until deployment. */
  readonly fairLaunchFactoryAddress: Hex | null;
  /** Tx that created the Fair Launch factory. */
  readonly fairLaunchFactoryDeploymentTx: Hex | null;
  /** Block the Fair Launch factory was deployed at. */
  readonly fairLaunchFactoryDeploymentBlock: bigint | null;
  /** keccak256 of the Fair Launch factory's DEPLOYED (runtime) bytecode. */
  readonly fairLaunchExpectedBytecodeHash: Hex | null;

  /** Whether the UI allows selecting/using this chain in THIS build. */
  readonly enabledInUi: boolean;
}

// ── Standard Token factory hashes ────────────────────────────────────────────
// keccak256 of the Standard Token factory runtime bytecode.
// Produced by solc 0.8.24, optimizer runs=200, metadata.bytecodeHash=none.
// Deployed 2026-07-14 to Robinhood Chain Testnet (chain 46630).
// Verified in A2 acceptance report: expectedFactoryRuntimeHash matches on-chain.
const STANDARD_TOKEN_FACTORY_HASH: Hex =
  "0xd20370f9e2c15067a4d5cedb4c058b7876e8023e7b91d104cf2436dceb89b57c";
// Reserved: legacy Standard Token factory hash retained for reference/tests.
void STANDARD_TOKEN_FACTORY_HASH;

// ── Canonical ETH-v1 FairLaunchFactory hash ──────────────────────────────────
// keccak256 of the deployed runtime bytecode at 0x60f2954A8E1D80D360Ba4b9F218Ff7Aa9dc085e8
// (chain 46630, block 93443149). Package: contracts-fair-launch-eth, solc 0.8.24.
// Oracle dependency: NONE. Economics: feeBps=100, creatorCurveBps=50,
// graduation 0.25 ETH, creation fee 0.0001 ETH (verified via getEconomicConfig()).
const ETH_V1_FACTORY_HASH: Hex =
  "0x3b749c6166634766232dfed1a8c552f1d901e662531d61d7c5ef804973d645f8";

// ── Fair Launch factory hashes ────────────────────────────────────────────────
// keccak256 of the FairLaunchFactory runtime bytecode (USD oracle model, D-18).
// Deployed 2026-07-15 to Robinhood Chain Testnet (chain 46630).
// solc 0.8.24, optimizer runs=200, viaIR=true, bytecodeHash=none.
// Compiled deployedBytecode hash for FairLaunchFactory (D-23 — fallback oracle price feature).
// Supersedes D-22 (0x7807A2Cc9Fef0186c9068E7CA65D0bDe513F3F35, block 90473046).
// Produced by solc 0.8.24, optimizer runs=200, viaIR=true.
const FAIR_LAUNCH_FACTORY_HASH: Hex =
  "0xc856bc03f49dc4f63e50f697b2563dfb96658ef1a78eb71f647a054c2b56ac7d";

export const ROBINHOOD_TESTNET: ChainConfig = {
  key: "robinhoodTestnet",
  label: "Robinhood Chain Testnet",
  chainId: 46630,
  rpcUrl:
    import.meta.env.VITE_TESTNET_RPC ?? "https://rpc.testnet.chain.robinhood.com",
  rpcFallbackUrl: import.meta.env.VITE_TESTNET_RPC_FALLBACK as string | undefined,
  explorerUrl: "https://explorer.testnet.chain.robinhood.com",
  explorerApiUrl: "https://explorer.testnet.chain.robinhood.com/api",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  faucetUrl: "https://faucet.testnet.chain.robinhood.com",

  // Canonical ETH-v1 FairLaunchFactory (oracle-free, contracts-fair-launch-eth).
  // Deployed 2026-07-25 per executive decision — replaces the Standard Token
  // factory slot as the launcher target. Legacy factories remain on-chain
  // untouched. Manifest: contracts-fair-launch-eth/deployments/robinhood-testnet.json
  factoryAddress:
    (import.meta.env.VITE_TESTNET_FACTORY_ADDRESS as Hex | undefined) ??
    "0x60f2954A8E1D80D360Ba4b9F218Ff7Aa9dc085e8",
  factoryDeploymentTx:
    (import.meta.env.VITE_TESTNET_FACTORY_DEPLOYMENT_TX as Hex | undefined) ??
    "0x4ccf1bf7d0e5744801ffd5aab120838a5f1d1d8c308cdb1320a86ed1d522603d",
  factoryDeploymentBlock:
    parseBlock(import.meta.env.VITE_TESTNET_FACTORY_DEPLOYMENT_BLOCK) ?? 93443149n,
  expectedFactoryBytecodeHash: ETH_V1_FACTORY_HASH,
  factoryVersion: "1.0.0-eth-v1",

  // Fair Launch factory — bonding-curve markets (USD oracle model, D-18).
  // Redeployed 2026-07-15 (D-23): adds owner-settable fallbackOraclePrice for oracle outages.
  // Supersedes D-22 (0x7807A2Cc9Fef0186c9068E7CA65D0bDe513F3F35, block 90473046).
  // MockV3Aggregator oracle at $2000/ETH. testnetScaleFactor=100.
  // graduationValuation=$500 USD, virtualReserve=$30 USD, MAX_PRICE_DEVIATION_BPS=2000.
  fairLaunchFactoryAddress:
    (import.meta.env.VITE_TESTNET_FL_FACTORY_ADDRESS as Hex | undefined) ??
    "0x0bEAbd940a1F040a0F5b1be6A861DaB979fcaaF6",
  fairLaunchFactoryDeploymentTx:
    (import.meta.env.VITE_TESTNET_FL_FACTORY_DEPLOYMENT_TX as Hex | undefined) ??
    "0x61c4321cbc351f1f51bc1dd19f5787776dafb9fa9205f78caadea343013aeeb9",
  fairLaunchFactoryDeploymentBlock:
    parseBlock(import.meta.env.VITE_TESTNET_FL_FACTORY_DEPLOYMENT_BLOCK) ?? 90489654n,
  fairLaunchExpectedBytecodeHash: FAIR_LAUNCH_FACTORY_HASH,

  enabledInUi: true,
};

// DEFINED BUT DISABLED. Present so config is complete; the UI must never let a
// user switch to or transact on this chain in this build.
export const ROBINHOOD_MAINNET: ChainConfig = {
  key: "robinhoodMainnet",
  label: "Robinhood Chain Mainnet",
  chainId: 4663,
  rpcUrl: "https://rpc.mainnet.chain.robinhood.com",
  explorerUrl: "https://robinhoodchain.blockscout.com",
  explorerApiUrl: "https://robinhoodchain.blockscout.com/api",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  factoryAddress: null,
  factoryDeploymentTx: null,
  factoryDeploymentBlock: null,
  expectedFactoryBytecodeHash: null,
  factoryVersion: "1.0.0",
  fairLaunchFactoryAddress: null,
  fairLaunchFactoryDeploymentTx: null,
  fairLaunchFactoryDeploymentBlock: null,
  fairLaunchExpectedBytecodeHash: null,
  enabledInUi: false, // HARD DISABLED for this build
};

/** The only chain the app operates on in this build. */
export const ACTIVE_CHAIN = ROBINHOOD_TESTNET;

/** All chains, for reference/config screens. */
export const ALL_CHAINS = [ROBINHOOD_TESTNET, ROBINHOOD_MAINNET] as const;
